import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useApiContext } from "../../../../../api/context";
import { ssoGroupsMappingText } from "../../../../../assets/texts/Settings/sso";
import { useSnackbar } from "../../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useCustomerContext } from "../../../../../Context/CustomerContext";
import { consoleErrorWithSentry } from "../../../../../utils/errorHandling";
import { getDefaultRoleRefByCustomer } from "../../../../../utils/permissions";

export type GroupApiMapping = {
  groupId: string;
  roleAtDoit: string;
  org: string;
};

export type GroupMapping = {
  groupId: string;
  role: string;
  organization: string;
};

export type SsoGroupMappingResponse = {
  customerId: string;
  providerAttributeKey: string;
  userGroups: GroupApiMapping[];
};

export const useSsoGroupMapping = (
  customerId: string,
  attributeKey: string,
  onAttributeKeyChange: (value: string) => void
) => {
  const api = useApiContext();
  const sharedSnackbar = useSnackbar();
  const { customer, organizations } = useCustomerContext();
  const defaultRoleId = getDefaultRoleRefByCustomer(customer)?.id;
  const rootOrg = "root";

  const [userGroups, setUserGroups] = useState<Record<string, GroupMapping>>({});
  const [items, setItems] = useState<Record<string, string[]>>({ main: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasGroupMappings, setHasGroupMappings] = useState(false);

  // Store initial state for comparison
  const initialStateRef = useRef<{
    userGroups: Record<string, GroupMapping>;
    items: Record<string, string[]>;
    attributeKey: string;
  }>({
    userGroups: {},
    items: { main: [] },
    attributeKey: "",
  });

  const [defaultMapping, setDefaultMapping] = useState<GroupMapping>({
    groupId: ssoGroupsMappingText.GROUP_ID_DEFAULT,
    role: defaultRoleId,
    organization: "",
  });

  // Helper function to check if current state differs from initial state
  const hasChanges = useMemo(() => {
    const currentState = {
      userGroups,
      items,
      attributeKey,
    };

    // Compare attributeKey first
    if (initialStateRef.current.attributeKey !== currentState.attributeKey) {
      return true;
    }

    // Compare items arrays
    if (initialStateRef.current.items.main.length !== currentState.items.main.length) {
      return true;
    }

    // Compare items order
    const initialOrder = initialStateRef.current.items.main;
    const currentOrder = currentState.items.main;
    const hasOrderChanged =
      initialOrder.length !== currentOrder.length || !initialOrder.every((id, index) => id === currentOrder[index]);
    if (hasOrderChanged) {
      return true;
    }

    // Compare userGroups
    const initialKeys = Object.keys(initialStateRef.current.userGroups);
    const currentKeys = Object.keys(currentState.userGroups);

    if (initialKeys.length !== currentKeys.length) {
      return true;
    }

    // Compare each mapping
    return currentKeys.some((key) => {
      const initial = initialStateRef.current.userGroups[key];
      const current = currentState.userGroups[key];

      return (
        !initial ||
        !current ||
        initial.groupId !== current.groupId ||
        initial.role !== current.role ||
        initial.organization !== current.organization
      );
    });
  }, [userGroups, items, attributeKey]);

  useEffect(() => {
    const checkHasGroupMappings = async () => {
      try {
        const response = await api.request<SsoGroupMappingResponse>({
          method: "GET",
          url: `/auth/sso-groups/${customerId}`,
        });
        setHasGroupMappings(!!response.data.userGroups.length);
      } catch (error) {
        consoleErrorWithSentry(error);
        setHasGroupMappings(false);
      }
    };
    checkHasGroupMappings();
  }, [customerId, api]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .request<SsoGroupMappingResponse>({
        method: "GET",
        url: `/auth/sso-groups/${customerId}`,
      })
      .then((response) => {
        const data = response.data;
        if (data?.providerAttributeKey) {
          onAttributeKeyChange(data.providerAttributeKey);
        }

        let initialUserGroups: GroupApiMapping[] = [];
        if (data?.userGroups && Array.isArray(data.userGroups)) {
          initialUserGroups = data.userGroups;
        }

        const initialMappings: Record<string, GroupMapping> = initialUserGroups.reduce(
          (acc, group, idx) => {
            acc[idx.toString()] = {
              groupId: group.groupId,
              role: group.roleAtDoit,
              organization: group.org,
            };
            return acc;
          },
          {} as Record<string, GroupMapping>
        );
        setUserGroups(initialMappings);

        const initialItemIds: string[] = initialUserGroups.map((_, idx) => idx.toString());
        setItems({ main: initialItemIds });

        // Store initial state
        initialStateRef.current = {
          userGroups: initialMappings,
          items: { main: initialItemIds },
          attributeKey: data?.providerAttributeKey || "",
        };

        setLoading(false);
      })
      .catch((err) => {
        if (err.response?.status !== 404) {
          setError(err.message);
        }
        setLoading(false);
      });
  }, [customerId, api, onAttributeKeyChange]);

  useEffect(() => {
    const parentOrg = organizations.find((org) => org.data.parent === null);
    const parentOrgId = parentOrg?.ref.id || "";
    setDefaultMapping((prev) => ({
      ...prev,
      role: defaultRoleId,
      organization: parentOrgId,
    }));
  }, [defaultRoleId, organizations]);

  const handleItemsOrder = (newItems: Record<string, string[]>) => {
    setItems(newItems);
  };

  const handleMappingChange = (id: string, field: keyof GroupMapping, value: string) => {
    setUserGroups((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleDelete = (idToDelete: string) => {
    try {
      setItems((prev) => ({
        ...prev,
        main: prev.main.filter((id) => id !== idToDelete),
      }));

      setUserGroups((prev) => {
        const newMappings = { ...prev };
        delete newMappings[idToDelete];
        return newMappings;
      });
    } catch (error) {
      alert(ssoGroupsMappingText.MSG_ERR_DELETING);
    }
  };

  const handleAddMapping = useCallback(() => {
    const newUserGroups: Record<string, GroupMapping> = {};
    const newItems: string[] = [];
    newUserGroups["0"] = { groupId: "", role: defaultRoleId, organization: rootOrg };
    newItems.push("0");

    Object.keys(userGroups).forEach((oldKey) => {
      const newKey = (parseInt(oldKey, 10) + 1).toString();
      newUserGroups[newKey] = userGroups[oldKey];
      newItems.push(newKey);
    });

    setUserGroups(newUserGroups);
    setItems({ main: newItems });
  }, [defaultRoleId, userGroups]);

  const handleSaveChanges = useCallback(async () => {
    try {
      setSaving(true);
      await api.request({
        method: "PUT",
        url: "/auth/sso-groups",
        data: {
          customerId,
          providerAttributeKey: attributeKey,
          userGroups: items.main.map((id) => {
            const group = userGroups[id];
            return {
              groupId: group.groupId,
              roleAtDoit: group.role,
              org: group.organization,
            };
          }),
        },
      });

      // Update initial state after successful save
      initialStateRef.current = {
        userGroups,
        items,
        attributeKey,
      };

      sharedSnackbar.onOpen({
        message: ssoGroupsMappingText.MSG_INFO_SAVED,
        variant: "success",
        autoHideDuration: 3000,
      });
    } catch (err) {
      sharedSnackbar.onOpen({
        message: ssoGroupsMappingText.MSG_ERR_NOT_SAVED(err instanceof Error ? err.message : "Unknown error"),
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setSaving(false);
    }
  }, [customerId, attributeKey, items, userGroups, api, sharedSnackbar]);

  const handleReset = useCallback(() => {
    // Reset all state back to initial values
    setUserGroups(initialStateRef.current.userGroups);
    setItems(initialStateRef.current.items);
    onAttributeKeyChange(initialStateRef.current.attributeKey);
  }, [onAttributeKeyChange]);

  return {
    hasGroupMappings,
    userGroups,
    items,
    loading,
    error,
    saving,
    defaultMapping,
    handleItemsOrder,
    handleMappingChange,
    handleDelete,
    handleAddMapping,
    handleSaveChanges,
    handleReset,
    isContentUpdated: hasChanges,
  };
};
